/**
 * normalizeData.js
 * Transforms the existing API schedule response into DiaryEvent objects.
 * Replaces V1's getEvent() function from NewDiary.js.
 */

import { computeBookingWindow, bookingSegments } from '../shared/computeBookingWindow';

const FALLBACK_COLORS = ['#D6D5EC', '#C8EEEE', '#D6D5EC', '#f5dabf'];
const WALKIN_CLIENT_ID = '64f5c7789a069cda0188fea3';

// Resolve setup/cleanup defaults from the booked-service entry, falling back
// to the embedded service definition if the booking doesn't carry overrides.
function resolveBufferFields(s) {
  return {
    setupTime: s.setupTime ?? s.service?.setupTime ?? 0,
    cleanupTime: s.cleanupTime ?? s.service?.cleanupTime ?? 0,
  };
}

/**
 * Normalize a single staff schedule API response into DiaryEvent[]
 *
 * @param {Object} schedule - { calendar: { calendar: [...], todayRota: {...} }, hairdresser: {...} }
 * @param {string} staffId - The hairdresser _id
 * @param {string} date - 'YYYY-MM-DD'
 * @returns {DiaryEvent[]}
 */
export function normalizeScheduleResponse(schedule, staffId, date) {
  const events = [];

  // Process calendar items (bookings, events, holidays)
  if (schedule?.calendar?.calendar?.length > 0) {
    schedule.calendar.calendar.forEach((item, i) => {
      const entry = item.list[0];
      if (!entry) return;

      const type = entry.type;
      const info = entry.info;

      if (type === 'booking') {
        const services = info.servicesBooked || [];
        const primaryColor = services[0]?.service?.diaryColour || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
        const isProcessing = info.isServiceProcessingTime === 1;

        // Pre-compute resolved buffers so they can also be exposed on the event
        // (multi-service rule: only first svc setup, only last svc cleanup).
        const enrichedServices = services.map((s) => {
          const { setupTime, cleanupTime } = resolveBufferFields(s);
          return { ...s, setupTime, cleanupTime };
        });
        const bookingWindow = computeBookingWindow(enrichedServices);

        const setupMinutesV1 = bookingWindow.setupMinutes;
        const cleanupMinutesV1 = bookingWindow.cleanupMinutes;
        const eventStart = item.startTime;
        const eventEnd = item.endTime;

        events.push({
          id: `${staffId}_booking_${i}`,
          staffId,
          date,
          type: 'booking',
          startMinutes: eventStart,
          endMinutes: eventEnd,
          bookingId: info._id,
          chainId: info._id, // all services in a booking share the booking _id
          isProcessingTime: isProcessing,
          title: getServiceName(item),
          clientName: info.user?.name || info.clientInfo?.name || (info.clientId === WALKIN_CLIENT_ID ? 'Walk-in' : ''),
          clientAvatar: info.user?.avatar || info.clientInfo?.avatar || '',
          clientId: info.user?._id || info.clientInfo?._id || info.clientId || '',
          services: enrichedServices.map((s, si) => ({
            serviceId: s.service?.id || s.service?._id || s.serviceId,
            serviceName: s.service?.serviceName || s.serviceName || '',
            servicePrice: parseFloat(s.servicePrice) || 0,
            serviceTime: Math.max(0, s.serviceTime || 0),
            processingTime: s.processingTime || 0,
            setupTime: s.setupTime || 0,
            cleanupTime: s.cleanupTime || 0,
            diaryColour: s.service?.diaryColour || FALLBACK_COLORS[si % FALLBACK_COLORS.length],
            hairdresserId: s.hairdresserId || info.hairdresserId,
            hairdresserName: s.hairdresserName || s.hairdresser?.name || '',
            serviceIndex: si,
            bookingId: info._id,
          })),
          // Expose buffers at the event level so EventBlock can render the
          // hatched setup/cleanup overlays without re-deriving them.
          setupMinutes: setupMinutesV1,
          cleanupMinutes: cleanupMinutesV1,
          diaryColour: isProcessing ? '#C36464' : primaryColor,
          status: info.bookingStatus,
          // Refunds on a completed sale (docs/refunds.md): drives the card's mark.
          refundStatus: info.refundStatus || null,
          refundedTotal: info.refundedTotal || 0,
          stripePaymentMethodId: info.stripePaymentMethodId || '',
          editable: true,
          resizable: true,
          raw: item,
        });
      }

      if (type === 'event') {
        events.push({
          id: `${staffId}_event_${i}`,
          staffId,
          date,
          type: 'event',
          startMinutes: item.startTime,
          endMinutes: item.endTime,
          eventId: info._id,
          title: info.title || 'Event',
          diaryColour: info.diaryColour || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
          emoji: info.emoji || null,
          // Surfaces the "still bookable" flag at the top level so EventBlock
          // and BookingDetailPanel can read it without diving into raw.info.
          // Defaults to false (slot blocked) which matches the create-form
          // default and the salon-owner expectation that events block bookings.
          isBookingAllowed: !!info.isBookingAllowed,
          editable: true,
          resizable: true,
          raw: item,
        });
      }

      if (type === 'holiday') {
        events.push({
          id: `${staffId}_holiday_${i}`,
          staffId,
          date,
          type: 'holiday',
          startMinutes: 0,
          endMinutes: 1440,
          eventId: info._id,
          title: info.title || info.info?.title || 'Leave',
          diaryColour: info.diaryColour || '#ff6b6b',
          emoji: info.emoji || null,
          leaveType: info.leaveType || info.info?.leaveType,
          editable: false,
          resizable: false,
          isFullDay: true,
          raw: item,
        });
      }
    });
  }

  // Process break times from today's rota
  if (schedule?.calendar?.todayRota?.breakTimes?.length > 0) {
    schedule.calendar.todayRota.breakTimes.forEach((item, i) => {
      events.push({
        id: `${staffId}_break_${i}`,
        staffId,
        date,
        type: 'break',
        startMinutes: item.startTime,
        endMinutes: item.endTime,
        title: 'Break',
        diaryColour: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
        editable: false,
        resizable: false,
        raw: item,
      });
    });
  }

  return events;
}

/**
 * Extract staff metadata from the rota response
 * @param {Object} hairdresser - Staff object from rota API
 * @param {string} date - 'YYYY-MM-DD'
 * @param {string} [dayName] - Optional pre-computed day name (e.g. 'mon'). If not provided, computed from date.
 */
export function normalizeStaffMember(hairdresser, date, dayName) {
  const rota = hairdresser.rotaInfo?.rota;
  if (!dayName) dayName = getDayName(date);
  let schedule = null;

  if (rota?.schedule?.[0]?.profiles) {
    schedule = rota.schedule[0].profiles.find(p => p.day === dayName) || null;
  }

  return {
    id: hairdresser._id,
    name: hairdresser.name || `${hairdresser.firstName || ''} ${hairdresser.lastName || ''}`.trim(),
    avatar: hairdresser.userImage?.circleImage || hairdresser.avatar || '',
    isWorking: !!schedule,
    startMinutes: schedule?.startTime ?? null,
    endMinutes: schedule?.endTime ?? null,
    isSalonOwner: hairdresser.isSalonOwner === 1,
    raw: hairdresser,
  };
}

/**
 * Calculate optimal time bounds from all staff schedules
 * Matches V1 logic: 2 hours before earliest start, 3-4 hours after latest end
 */
export function calculateTimeBounds(staffMembers) {
  let earliest = 1440;
  let latest = 0;

  staffMembers.forEach(staff => {
    if (staff.isWorking) {
      if (staff.startMinutes < earliest) earliest = staff.startMinutes;
      if (staff.endMinutes > latest) latest = staff.endMinutes;
    }
  });

  // If no working staff, default 8am-6pm
  if (earliest === 1440 || latest === 0) {
    return { start: 480, end: 1080 };
  }

  // 2 hours before earliest start
  let startHr = Math.floor(earliest / 60);
  startHr = startHr >= 2 ? startHr - 2 : 0;
  const viewStart = startHr * 60;

  // 3-4 hours after latest end
  let endHr = Math.floor(latest / 60);
  const endMin = latest % 60;
  endHr = endMin === 0 ? endHr + 3 : endHr + 4;
  if (endHr > 24) endHr = 24;
  const viewEnd = endHr * 60;

  return { start: viewStart, end: viewEnd };
}

// ── Utilities ──────────────────────────────────────────────

function getServiceName(item) {
  let name = '';
  if (item.list[0].info?.servicesBooked) {
    item.list[0].info.servicesBooked.forEach(service => {
      if (name) name += ', ';
      name += service?.service?.serviceName || '';
    });
  }
  return name;
}

function getDayName(dateStr) {
  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const d = new Date(dateStr + 'T12:00:00'); // noon to avoid timezone issues
  return days[d.getDay()];
}

// ── V2 Batch Diary API Normalizers ─────────────────────────

/**
 * Normalize the V2 /diary batch response into the staff + events format
 * that DiaryV2.js already consumes.
 *
 * @param {Object} diaryData - { staff, dates, timezone }
 * @param {string} date - 'YYYY-MM-DD' (for day view, the selected date)
 * @returns {{ staffMembers: StaffMember[], eventsByStaff: { [staffId]: DiaryEvent[] } }}
 */
export function normalizeDiaryResponse(diaryData, date) {
  const { staff = [], dates = {} } = diaryData;
  const dayData = dates[date] || {};

  const staffMembers = staff.map(s => {
    const staffDay = dayData[s.id] || {};
    const rota = staffDay.rota;
    return {
      id: s.id,
      name: s.name,
      firstName: s.firstName,
      lastName: s.lastName,
      avatar: s.avatar || '',
      isWorking: staffDay.isWorking || false,
      startMinutes: rota?.startTime ?? null,
      endMinutes: rota?.endTime ?? null,
      isSalonOwner: s.isSalonOwner,
      bookingInterval: s.bookingInterval,
      raw: s,
    };
  });

  const eventsByStaff = {};
  for (const s of staff) {
    const staffDay = dayData[s.id] || {};
    eventsByStaff[s.id] = normalizeDiaryDayForStaff(s.id, date, staffDay);
  }

  return { staffMembers, eventsByStaff };
}

/**
 * Normalize a full week from the V2 batch response for a single staff member.
 * Used by week view.
 *
 * @param {Object} diaryData - { staff, dates, timezone }
 * @param {string} staffId
 * @returns {{ weekData: { [date]: DiaryEvent[] }, weekStaffInfo: { [date]: { isWorking, startMinutes, endMinutes } } }}
 */
export function normalizeDiaryWeekResponse(diaryData, staffId) {
  const { dates = {} } = diaryData;
  const weekData = {};
  const weekStaffInfo = {};

  for (const [dateStr, staffMap] of Object.entries(dates)) {
    const staffDay = staffMap[staffId] || {};
    weekData[dateStr] = normalizeDiaryDayForStaff(staffId, dateStr, staffDay);

    const rota = staffDay.rota;
    weekStaffInfo[dateStr] = {
      isWorking: staffDay.isWorking || false,
      startMinutes: rota?.startTime ?? null,
      endMinutes: rota?.endTime ?? null,
    };
  }

  return { weekData, weekStaffInfo };
}

/**
 * Convert a single staff's day data from V2 API into DiaryEvent[].
 * Produces the same shape as normalizeScheduleResponse() so the UI is unchanged.
 */
function normalizeDiaryDayForStaff(staffId, date, staffDay) {
  const events = [];
  const { bookings = [], events: dayEvents = [], holidays = [], rota } = staffDay;

  // Bookings
  bookings.forEach((booking, i) => {
    const services = booking.servicesBooked || [];
    const primaryColor = services[0]?.diaryColour || services[0]?.service?.diaryColour || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
    const isProcessing = booking.isServiceProcessingTime === 1;
    const startMinutes = booking.bookingSlot?.[0] ?? 0;
    const endMinutes = booking.bookingSlot?.[1] ?? 0;

    const serviceNames = services.map(s => s.serviceName || '').filter(Boolean).join(', ');

    // Resolve buffers up-front so they're available everywhere below.
    // The booking entry wins if the backend echoes a per-appointment override;
    // otherwise we fall back to the service-definition default.
    services.forEach((s) => {
      const { setupTime, cleanupTime } = resolveBufferFields(s);
      s.setupTime = setupTime;
      s.cleanupTime = cleanupTime;
    });

    // Multi-service rule: only first svc setup, only last svc cleanup.
    const firstSetupMinutes = Math.max(0, services[0]?.setupTime || 0);
    const lastCleanupMinutes = Math.max(0, services[services.length - 1]?.cleanupTime || 0);
    const slotDurationRaw = Math.max(0, endMinutes - startMinutes);

    // Guard: if the booking's reserved slot isn't wide enough to contain the
    // resolved setup + service + cleanup, treat the buffers as 0 for display.
    // Catches legacy bookings created before this feature where bookingSlot
    // didn't include the buffer window.
    const totalSvcTimeForBuffer = services.reduce((sum, s) => sum + Math.max(0, s.serviceTime || 0), 0);
    const slotFitsBuffers = slotDurationRaw >= (firstSetupMinutes + totalSvcTimeForBuffer + lastCleanupMinutes);
    const setupMinutes = slotFitsBuffers ? firstSetupMinutes : 0;
    const cleanupMinutes = slotFitsBuffers ? lastCleanupMinutes : 0;
    const visualStartMinutes = startMinutes;
    const visualEndMinutes = endMinutes;

    // Detect processing gaps: either explicit processingTime on services,
    // or inferred from the booking slot being longer than total service time
    // (after stripping the setup/cleanup window).
    //
    // Processing is a free, bookable gap — never solid booked time — for
    // SINGLE-service bookings too (e.g. a colour-only appt: apply →
    // develop → finish). The split loop already renders one service
    // correctly: the active block, then processingTime as trailing empty
    // space. So this is NOT gated on services.length > 1 — any service
    // with processingTime > 0 splits. (The *inferred* fallback below
    // stays multi-only: it divides by services.length - 1.)
    let hasProcessingGap = services.some(s => (s.processingTime || 0) > 0);
    if (!hasProcessingGap && services.length > 1) {
      const totalSvcTime = services.reduce((sum, s) => sum + Math.max(0, s.serviceTime || 0), 0);
      const slotMinusBuffers = slotDurationRaw - setupMinutes - cleanupMinutes;
      if (slotMinusBuffers > totalSvcTime && totalSvcTime > 0) {
        // Distribute the gap evenly as processing time on all but the last service
        const gap = slotMinusBuffers - totalSvcTime;
        const gapPerService = Math.round(gap / (services.length - 1));
        services.forEach((s, si) => {
          if (si < services.length - 1) {
            s.processingTime = gapPerService;
          }
        });
        hasProcessingGap = true;
      }
    }

    // Common fields for all blocks from this booking
    const commonFields = {
      staffId,
      date,
      type: 'booking',
      // Rooms/equipment this booking holds (snapshots) — staff-lane badges
      // and the resource-lane fan-out both read these.
      resources: booking.resources || [],
      bookingId: booking._id,
      chainId: booking._id,
      clientName: booking.user?.name || booking.clientInfo?.name || (booking.clientId === WALKIN_CLIENT_ID ? 'Walk-in' : ''),
      clientAvatar: booking.user?.avatar || booking.clientInfo?.avatar || '',
      clientId: booking.user?._id || booking.clientInfo?._id || booking.clientId || '',
      status: booking.bookingStatus,
      refundStatus: booking.refundStatus || null,
      refundedTotal: booking.refundedTotal || 0,
      stripePaymentMethodId: booking.stripePaymentMethodId || '',
      // Online (1) vs in-salon (0). The list feed carries this reliably; the
      // by-id detail call has not always echoed it, so the detail panel falls
      // back to this copy. Left undefined when the feed omits it.
      isOnlineBooking: booking.isOnlineBooking,
      // Repeat bookings: the card shows a repeat mark
      seriesId: booking.seriesId || null,
      editable: true,
      resizable: true,
      raw: {
        startTime: startMinutes,
        endTime: endMinutes,
        list: [{
          type: 'booking',
          info: booking,
        }],
      },
      services: services.map((s, si) => ({
        serviceId: s.service || s._id,
        serviceName: s.serviceName || '',
        servicePrice: parseFloat(s.servicePrice) || 0,
        serviceTime: Math.max(0, s.serviceTime || 0),
        processingTime: s.processingTime || 0,
        finishingTime: s.finishingTime || 0,
        setupTime: s.setupTime || 0,
        cleanupTime: s.cleanupTime || 0,
        diaryColour: s.diaryColour || s.service?.diaryColour || FALLBACK_COLORS[si % FALLBACK_COLORS.length],
        hairdresserId: staffId,
        hairdresserName: '',
        serviceIndex: si,
        bookingId: booking._id,
      })),
    };

    // Where the first actual service begins. The backend already widens
    // bookingSlot to include the buffer, so service starts after setup.
    const serviceStart = startMinutes + setupMinutes;

    if (hasProcessingGap) {
      // Split into separate blocks: one per service, plus a finishing block
      // after a service's processing when it has finishingTime (see
      // bookingSegments). The first segment starts after the setup buffer and
      // the last segment ends before the cleanup buffer so the buffers stay
      // attached to the overall booking, not in the gaps.
      let currentStart = serviceStart;
      const segments = bookingSegments(services);
      const segmentCount = segments.length;
      segments.forEach((seg, segIdx) => {
        const si = seg.serviceIndex;
        const s = services[si];
        const isFinishing = seg.role === 'finishing';
        const blockTime = Math.max(0, (isFinishing ? s.finishingTime : s.serviceTime) || 0);
        const blockEnd = currentStart + blockTime;
        const svcColour = s.diaryColour || s.service?.diaryColour || FALLBACK_COLORS[si % FALLBACK_COLORS.length];
        const isFirst = segIdx === 0;
        const isLast = segIdx === segmentCount - 1;

        // Block (gap after it is implicit from the next block's startMinutes)
        events.push({
          ...commonFields,
          id: `${staffId}_booking_${i}_svc_${si}${isFinishing ? '_fin' : ''}`,
          // Extend the first segment back to cover the setup hatching, and the
          // last segment forward to cover the cleanup hatching, so the buffer
          // stays visually attached to the booking.
          startMinutes: isFirst ? currentStart - setupMinutes : currentStart,
          endMinutes: isLast ? blockEnd + cleanupMinutes : blockEnd,
          isProcessingTime: false,
          title: isFinishing ? `${s.serviceName || serviceNames} · finish` : (s.serviceName || serviceNames),
          diaryColour: svcColour,
          isServiceSegment: true,
          segmentIndex: segIdx,
          segmentCount,
          segmentRole: seg.role,
          serviceIndex: si,
          setupMinutes: isFirst ? setupMinutes : 0,
          cleanupMinutes: isLast ? cleanupMinutes : 0,
        });

        // Processing sits after the service block — before its finishing
        // block if it has one, otherwise before the next service.
        currentStart = blockEnd + (isFinishing ? 0 : (s.processingTime || 0));
      });
    } else {
      // Single block for the whole booking (no processing gaps)
      events.push({
        ...commonFields,
        id: `${staffId}_booking_${i}`,
        startMinutes: visualStartMinutes,
        endMinutes: visualEndMinutes,
        isProcessingTime: isProcessing,
        title: serviceNames,
        diaryColour: isProcessing ? '#C36464' : primaryColor,
        setupMinutes,
        cleanupMinutes,
      });
    }

    // Continue with raw block (already included in commonFields.raw)
    // The raw block closing was part of the original push - skip the duplicate
    return; // forEach callback return
  });

  // Events
  dayEvents.forEach((evt, i) => {
    events.push({
      id: `${staffId}_event_${i}`,
      staffId,
      date,
      type: 'event',
      startMinutes: evt.startTime,
      endMinutes: evt.endTime,
      eventId: evt._id,
      title: evt.title || 'Event',
      diaryColour: evt.diaryColour || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      emoji: evt.emoji || null,
      editable: true,
      resizable: true,
      raw: {
        startTime: evt.startTime,
        endTime: evt.endTime,
        list: [{
          type: 'event',
          info: evt,
        }],
      },
    });
  });

  // Holidays - use rota times if working, otherwise fit within typical diary viewport
  holidays.forEach((hol, i) => {
    events.push({
      id: `${staffId}_holiday_${i}`,
      staffId,
      date,
      type: 'holiday',
      startMinutes: 0,
      endMinutes: 1440,
      eventId: hol._id,
      title: hol.title || 'Leave',
      diaryColour: hol.diaryColour || '#ff6b6b',
      emoji: hol.emoji || null,
      // Surface leave kind so BookingDetailPanel's event.leaveType
      // resolves (sick / holiday / other) instead of defaulting to holiday.
      leaveType: hol.leaveType,
      editable: false,
      resizable: false,
      isFullDay: true,
      raw: {
        startTime: 0,
        endTime: 1440,
        list: [{
          type: 'holiday',
          info: hol,
        }],
      },
    });
  });

  // Break times from rota
  if (rota?.breakTimes?.length > 0) {
    rota.breakTimes.forEach((bt, i) => {
      events.push({
        id: `${staffId}_break_${i}`,
        staffId,
        date,
        type: 'break',
        startMinutes: bt.startTime,
        endMinutes: bt.endTime,
        title: bt.title || 'Break',
        diaryColour: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
        editable: false,
        resizable: false,
        raw: bt,
      });
    });
  }

  return events;
}

export { FALLBACK_COLORS };


/**
 * Resource lanes (docs/resources.md §5): each room/machine becomes a pseudo-
 * staff lane, its events cloned from every stylist's bookings that hold it.
 * The same machinery that renders staff columns renders these — that's the
 * point.
 *
 * R7: a booking whose stylist the viewer cannot see renders as a REDACTED
 * busy block — "Room 2 · busy till 3:15" carries no client, service or
 * stylist detail. visibleStaffIds is the diary-sharing verdict already
 * applied to the staff columns; the lanes must not out-talk it.
 *
 * v1 lanes are read-only (laneReadOnly): no drag, no resize. Reassigning a
 * room rides the booking panel; cross-lane drag is a fast-follow once the
 * solver-backed reassign endpoint exists.
 */
const HOURS_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** A unit's window for one date. null = closed all day. */
function unitWindowForDate(unit, date) {
  const h = unit && unit.hours;
  if (!h || h.mode !== 'custom' || !h.days) return { start: 0, end: 24 * 60, custom: false };
  const d = h.days[HOURS_DAY_KEYS[new Date(date + 'T00:00:00').getDay()]] || {};
  if (d.open === false) return null;
  const start = Math.max(0, parseInt(d.start, 10) || 0);
  const end = Math.min(1440, parseInt(d.end, 10) || 1440);
  if (end <= start) return null; // inverted fails closed, matching the solver
  return { start, end, custom: true };
}

export function buildResourceLanes(resourceUnits, groupsById, staffMembers, eventsByStaff, visibleStaffIds, resourceEvents, date) {
  const visible = new Set((visibleStaffIds || []).map(String));
  const noVisibilityData = visible.size === 0; // empty = manager/no restriction
  const staffNameById = {};
  staffMembers.forEach(sm => { staffNameById[String(sm.id)] = sm.name; });

  const laneStaff = [];
  const eventsByLane = {};
  const unitById = {};
  (resourceUnits || []).forEach(u => { unitById[String(u._id)] = u; });

  (resourceUnits || []).forEach(u => {
    if (u.isRemoved || u.isActive === false) return;
    const laneId = 'res_' + String(u._id);
    const group = groupsById[String(u.groupId)] || {};
    // Opening hours drive the SAME fields staff columns grey out on — the
    // lane shows closed time exactly the way a stylist's off-shift renders.
    const win = unitWindowForDate(u, date);
    laneStaff.push({
      id: laneId,
      name: u.name,
      isResource: true,
      resourceId: String(u._id),
      groupName: group.name || '',
      turnoverTime: u.turnoverTime || 0,
      diaryColour: u.diaryColour || '#8B8B93',
      avatar: (u.images && u.images[0] && u.images[0].url) || '',
      isWorking: !!win,
      hasCustomHours: !!(win && win.custom) || !win,
      startMinutes: win ? win.start : 0,
      endMinutes: win ? win.end : 0,
    });
    eventsByLane[laneId] = [];
  });
  if (!laneStaff.length) return { laneStaff, eventsByLane };

  Object.keys(eventsByStaff).forEach(staffId => {
    const canSee = noVisibilityData || visible.has(String(staffId));
    (eventsByStaff[staffId] || []).forEach(ev => {
      if (ev.type !== 'booking' || !Array.isArray(ev.resources) || !ev.resources.length) return;
      ev.resources.forEach(r => {
        const laneId = 'res_' + String(r.resourceId);
        if (!eventsByLane[laneId]) return; // deleted/hidden unit — snapshot stays, lane doesn't
        const unit = unitById[String(r.resourceId)];
        const clone = {
          ...ev,
          staffId: laneId,
          isResourceLane: true,
          laneReadOnly: true,
          laneStylistName: staffNameById[String(staffId)] || '',
          diaryColour: (unit && unit.diaryColour) || ev.diaryColour,
        };
        if (!canSee) {
          clone.redacted = true;
          clone.clientName = 'Busy';
          clone.clientAvatar = '';
          clone.clientId = '';
          clone.title = '';
          clone.serviceNames = '';
          clone.laneStylistName = '';
          clone.services = [];
        }
        eventsByLane[laneId].push(clone);
      });
    });
  });
  // Room-blocking events (all-day by default) render as event blocks on the
  // lane. Salon-operational — no R7 redaction. Read-only on the lane; they
  // are managed from the event panel / events list.
  (resourceEvents || []).forEach((evt, i) => {
    const laneId = 'res_' + String(evt.resourceId);
    if (!eventsByLane[laneId]) return;
    const unit = unitById[String(evt.resourceId)];
    eventsByLane[laneId].push({
      id: laneId + '_revent_' + i,
      staffId: laneId,
      type: 'event',
      isResourceLane: true,
      laneReadOnly: true,
      startMinutes: evt.startTime,
      endMinutes: evt.endTime,
      eventId: evt._id,
      title: evt.title || 'Unavailable',
      diaryColour: evt.diaryColour || (unit && unit.diaryColour) || '#8B8B93',
      editable: false,
      resizable: false,
    });
  });

  return { laneStaff, eventsByLane };
}

/** Colour the staff-lane room badges from the live unit list. */
export function enrichEventResourceColours(eventsByStaff, resourceUnits) {
  const colourById = {};
  (resourceUnits || []).forEach(u => { colourById[String(u._id)] = u.diaryColour || '#8B8B93'; });
  Object.keys(eventsByStaff).forEach(staffId => {
    (eventsByStaff[staffId] || []).forEach(ev => {
      (ev.resources || []).forEach(r => { r.diaryColour = colourById[String(r.resourceId)] || '#8B8B93'; });
    });
  });
}
